import React, { useEffect, useState, useContext, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthContext, subscribeDebug, getDebugLogs } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';

const DebugPanel = ({ token }) => {
  const [logs, setLogs] = useState(getDebugLogs());
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const unsub = subscribeDebug((entry) => {
      if (entry.level === 'clear') {
        setLogs([]);
      } else {
        setLogs(prev => [...prev, entry]);
      }
    });
    return unsub;
  }, []);

  const levelColors = {
    info: 'text-blue-400', warn: 'text-yellow-400',
    error: 'text-red-400', success: 'text-green-400'
  };
  const levelIcons = {
    info: 'ℹ️', warn: '⚠️', error: '❌', success: '✅'
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-gray-900 text-white text-xs font-mono px-3 py-2 rounded-lg shadow-lg border border-gray-700 hover:bg-gray-800 transition-colors flex items-center gap-2"
      >
        <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
        Debug ({logs.length})
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-950 border-t border-gray-700 shadow-2xl" style={{ height: '50vh' }}>
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-white text-xs font-mono font-bold">Confirm Email Debug</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 text-[10px] font-mono truncate max-w-[200px]">
            Token: {token ? `${token.substring(0, 8)}...` : 'NONE'}
          </span>
          <button
            onClick={() => setIsOpen(false)}
            className="text-gray-400 hover:text-white text-xs font-mono px-2 py-1 rounded bg-gray-800 hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
      <div className="overflow-y-auto p-3 font-mono text-xs" style={{ height: 'calc(50vh - 40px)' }}>
        {logs.length === 0 && (
          <div className="text-gray-600 text-center py-4">No logs yet.</div>
        )}
        {logs.map((log) => (
          <div key={log.id} className="mb-1.5 leading-relaxed">
            <span className="text-gray-600">{log.timestamp}</span>{' '}
            <span className={levelColors[log.level] || 'text-gray-300'}>
              {levelIcons[log.level] || '•'} [{log.level?.toUpperCase()}]
            </span>{' '}
            <span className="text-gray-200">{log.message}</span>
            {log.data && (
              <pre className="mt-1 ml-4 text-gray-500 whitespace-pre-wrap break-all text-[10px]">{log.data}</pre>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const ConfirmEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { confirmEmail } = useContext(AuthContext);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [token, setToken] = useState('');

  // ── CRITICAL: useRef guard to prevent duplicate confirmation calls ──
  const hasConfirmedRef = useRef(false);
  const confirmEmailRef = useRef(confirmEmail);

  useEffect(() => {
    confirmEmailRef.current = confirmEmail;
  }, [confirmEmail]);

  useEffect(() => {
    if (hasConfirmedRef.current) return;

    let extractedToken = searchParams.get('token');

    if (!extractedToken && window.location.hash) {
      const hashPart = window.location.hash;
      const queryStart = hashPart.indexOf('?');
      if (queryStart !== -1) {
        const queryString = hashPart.substring(queryStart + 1);
        const params = new URLSearchParams(queryString);
        extractedToken = params.get('token');
      }
    }

    if (!extractedToken) {
      const fullUrl = window.location.href;
      const tokenMatch = fullUrl.match(/[?&]token=([^&#]+)/);
      if (tokenMatch) {
        extractedToken = decodeURIComponent(tokenMatch[1]);
      }
    }

    setToken(extractedToken || '');

    if (!extractedToken) {
      setStatus('error');
      setMessage('No confirmation token found in the URL. Please check your email and click the link again.');
      return;
    }

    hasConfirmedRef.current = true;

    const confirm = async () => {
      const result = await confirmEmailRef.current(extractedToken);

      if (result.success) {
        // Check if this is an "already confirmed" response
        if (result.message === 'already_confirmed' || result.display) {
          setStatus('already-confirmed');
          setMessage(result.display || 'Your email is already confirmed!');
        } else {
          setStatus('success');
          setMessage(result.message || 'Email confirmed successfully!');
        }
      } else {
        const errMsg = (result.error || '').toLowerCase();
        if (errMsg.includes('already confirmed') || errMsg.includes('already been confirmed')) {
          setStatus('already-confirmed');
          setMessage('Your email is already confirmed! You can sign in to your account.');
        } else {
          setStatus('error');
          setMessage(result.error || 'Confirmation failed. Please try again.');
        }
      }
    };

    confirm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = async () => {
    if (!token) return;
    setStatus('loading');
    setMessage('');

    const result = await confirmEmailRef.current(token);
    if (result.success) {
      if (result.message === 'already_confirmed' || result.display) {
        setStatus('already-confirmed');
        setMessage(result.display || 'Your email is already confirmed!');
      } else {
        setStatus('success');
        setMessage(result.message || 'Email confirmed successfully!');
      }
    } else {
      setStatus('error');
      setMessage(result.error || 'Confirmation failed.');
    }
  };

  return (
    <div className="mobile-container bg-white flex flex-col">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex-1 flex flex-col items-center justify-center px-6"
      >
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin mb-6" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirming your email...</h1>
            <p className="text-gray-500 text-center">This will only take a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="w-24 h-24 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-100"
            >
              <SafeIcon name="Check" className="text-green-500 w-12 h-12" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-gray-900 mb-3 text-center"
            >
              Email Confirmed! 🎉
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-gray-500 text-center mb-8 max-w-xs leading-relaxed"
            >
              {message}
            </motion.p>
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              onClick={() => navigate('/login')}
              className="w-full max-w-xs bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200"
            >
              Go to Login
            </motion.button>
          </>
        )}

        {status === 'already-confirmed' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-blue-100"
            >
              <SafeIcon name="CheckCircle" className="text-blue-500 w-12 h-12" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-gray-900 mb-3 text-center"
            >
              You're all set! ✓
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-gray-500 text-center mb-6 max-w-xs leading-relaxed"
            >
              Your email has already been confirmed and your account is ready to use.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 }}
              className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-8 w-full max-w-xs text-center"
            >
              <p className="text-sm text-green-700 font-medium">
                You've successfully signed up for DayClap! 🎉
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="w-full max-w-xs space-y-3"
            >
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200 flex items-center justify-center gap-2"
              >
                <SafeIcon name="LogIn" className="w-5 h-5" />
                Sign In to Your Account
              </button>
              <button
                onClick={() => navigate('/')}
                className="w-full text-gray-500 font-medium py-2 hover:text-pink-500 transition-colors"
              >
                Go to DayClap Home
              </button>
            </motion.div>
          </>
        )}

        {status === 'error' && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="w-24 h-24 bg-gradient-to-br from-red-100 to-rose-100 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-red-100"
            >
              <SafeIcon name="X" className="text-red-500 w-12 h-12" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl font-bold text-gray-900 mb-3 text-center"
            >
              Confirmation Failed
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-gray-500 text-center mb-4 max-w-xs leading-relaxed"
            >
              {message}
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-6 w-full max-w-xs"
            >
              <p className="text-[10px] text-gray-400 font-mono mb-1">Debug Info:</p>
              <p className="text-[10px] text-gray-500 font-mono break-all">
                Token: {token ? `${token.substring(0, 8)}...${token.substring(token.length - 4)}` : 'NONE'}
              </p>
              <p className="text-[10px] text-gray-500 font-mono">
                URL: {window.location.href.substring(0, 60)}...
              </p>
            </motion.div>

            <div className="flex flex-col gap-3 w-full max-w-xs">
              {token && (
                <button
                  onClick={handleRetry}
                  className="w-full bg-gradient-to-r from-pink-500 to-rose-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-pink-200"
                >
                  Try Again
                </button>
              )}
              <button
                onClick={() => navigate('/signup')}
                className="w-full bg-gray-100 text-gray-700 font-semibold py-4 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Sign Up Again
              </button>
              <button
                onClick={() => navigate('/login')}
                className="w-full text-gray-500 font-medium py-2"
              >
                Go to Login
              </button>
            </div>
          </>
        )}
      </motion.div>
      <DebugPanel token={token} />
    </div>
  );
};

export default ConfirmEmail;